import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatDate, formatDateTime } from "@/lib/dates";
import { toast } from "sonner";
import { CreditRechargeDialog, CreditNoteActions } from "@/components/CreditRechargeDialog";
import {
  Wallet, Search, RefreshCw, Download, History, Mail, ExternalLink,
  CircleDollarSign, Clock, AlertTriangle, FileText, MapPin, Building2, ReceiptText,
} from "lucide-react";

const money = (n) => `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const BUCKET = {
  current: { label: "Current", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  due_soon: { label: "Due Soon", cls: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  overdue: { label: "Overdue", cls: "bg-red-100 text-red-700 border-red-200", dot: "bg-red-500" },
};

export default function InvoicesAwaitingPayment() {
  const [data, setData] = useState({ invoices: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("due_date");
  const [busy, setBusy] = useState("");
  const [auditId, setAuditId] = useState(null);
  const [confirmInv, setConfirmInv] = useState(null);
  const [creditInv, setCreditInv] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/invoices/awaiting-payment").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const refreshXero = async (inv) => {
    setBusy(`refresh-${inv.id}`);
    try {
      const r = await api.post(`/invoices/${inv.id}/refresh-xero`);
      if (r.data?.status === "paid") toast.success(`${inv.invoice_number} is PAID in Xero — moved to Paid Invoices`);
      else toast.success("Refreshed status & payment from Xero");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const sendReminder = async (inv) => {
    setConfirmInv(null);
    setBusy(`reminder-${inv.id}`);
    try {
      const r = await api.post(`/invoices/${inv.id}/send-reminder`, { confirm: true });
      toast.success(r.data.message);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  };
  const downloadInvoicePdf = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    }
    catch { toast.error("Could not generate invoice PDF"); }
  };
  const downloadDocketPdf = async (jid, inv) => {
    try {
      const res = await api.get(`/jobs/${jid}/docket`, { responseType: "blob", params: inv?.recharged_from ? { invoice_id: inv.id } : undefined });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Service-Docket-${inv?.invoice_number || jid}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    }
    catch { toast.error("Could not generate docket PDF"); }
  };
  const openInXero = (inv) => window.open(`https://go.xero.com/app/invoicing/view/${inv.xero_invoice_id}`, "_blank", "noopener");
  const downloadListPdf = async () => {
    setBusy("list-pdf");
    try {
      const res = await api.get("/invoices/awaiting-payment/pdf", { params: { filter, q: q.trim(), sort }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `awaiting-payment-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Awaiting Payment PDF downloaded");
    } catch { toast.error("Could not generate the Awaiting Payment PDF"); }
    finally { setBusy(""); }
  };

  const rows = useMemo(() => {
    let list = [...(data.invoices || [])];
    if (filter !== "all") list = list.filter((i) => i.due_bucket === filter);
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((i) =>
      (i.invoice_number || "").toLowerCase().includes(s) ||
      (i.billing_company || i.customer_name || "").toLowerCase().includes(s) ||
      (i.site_address || i.project_name || "").toLowerCase().includes(s));
    const cmp = {
      due_date: (a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999"),
      customer: (a, b) => (a.billing_company || a.customer_name || "").localeCompare(b.billing_company || b.customer_name || ""),
      value: (a, b) => (b.total_inc_gst || 0) - (a.total_inc_gst || 0),
      outstanding: (a, b) => (b.outstanding || 0) - (a.outstanding || 0),
    }[sort];
    return list.sort(cmp);
  }, [data, filter, q, sort]);

  const sm = data.summary || {};
  const Stat = ({ icon: Icon, label, value, sub, cls, tid }) => (
    <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm flex items-center gap-3" data-testid={tid}>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${cls}`}><Icon className="w-5 h-5" /></div>
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
        <p className="text-xl font-heading font-bold text-navy">{value}</p>
        {sub != null && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
  const invLabel = (n) => `${n ?? 0} ${(n ?? 0) === 1 ? "invoice" : "invoices"}`;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="awaiting-payment-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center"><Wallet className="w-6 h-6 text-emerald-600" /></div>
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy">Invoices Awaiting Payment</h1>
          <p className="text-slate-500 mt-0.5">Accounts Receivable — monitor unpaid invoices, send reminders and track overdue accounts.</p>
        </div>
      </div>
        <Button data-testid="ar-download-pdf" variant="outline" onClick={downloadListPdf} disabled={busy === "list-pdf" || loading}>
          <Download className="w-4 h-4 mr-2" />{busy === "list-pdf" ? "Preparing…" : "Download PDF"}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={CircleDollarSign} label="Current" value={money(sm.current_value)} sub={invLabel(sm.current)} cls="bg-emerald-50 text-emerald-600" tid="stat-current" />
        <Stat icon={Clock} label="Due Soon" value={money(sm.due_soon_value)} sub={invLabel(sm.due_soon)} cls="bg-amber-50 text-amber-600" tid="stat-due-soon" />
        <Stat icon={AlertTriangle} label="Overdue" value={money(sm.overdue_value)} sub={invLabel(sm.overdue)} cls="bg-red-50 text-red-600" tid="stat-overdue" />
        <Stat icon={Wallet} label="Outstanding Value" value={money(sm.outstanding_value)} sub={invLabel(sm.count)} cls="bg-navy/5 text-navy" tid="stat-outstanding" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="ar-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, invoice # or site address" className="pl-9" />
        </div>
        <div className="flex gap-1.5" data-testid="ar-filters">
          {[["all", "All"], ["current", "Current"], ["due_soon", "Due Soon"], ["overdue", "Overdue"]].map(([k, l]) => (
            <button key={k} data-testid={`ar-filter-${k}`} onClick={() => setFilter(k)}
              className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${filter === k ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-navy/40"}`}>{l}</button>
          ))}
        </div>
        <select data-testid="ar-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-600 outline-none focus:border-navy">
          <option value="due_date">Sort: Due Date</option>
          <option value="customer">Sort: Customer</option>
          <option value="value">Sort: Invoice Value</option>
          <option value="outstanding">Sort: Outstanding</option>
        </select>
      </div>

      {loading ? <p className="text-slate-400 text-sm">Loading…</p> : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center text-slate-400" data-testid="ar-empty">
          <Wallet className="w-10 h-10 mx-auto mb-3 opacity-40" />
          No invoices awaiting payment{q || filter !== "all" ? " match your filters" : ""}. Invoices appear here once sent via “Send Invoice & Sync to Xero”.
        </div>
      ) : (
        <div className="space-y-4">
          {rows.map((inv) => {
            const b = BUCKET[inv.due_bucket] || BUCKET.current;
            return (
              <div key={inv.id} data-testid={`ar-card-${inv.id}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <div className={`h-1 ${b.dot}`} />
                <div className="p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading font-bold text-navy text-lg">{inv.invoice_number}</span>
                        <span data-testid={`ar-badge-${inv.id}`} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${b.cls}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${b.dot}`} />{inv.due_label}
                        </span>
                        {inv.reminder_count > 0 && <span className="text-[11px] text-slate-400">{inv.reminder_count} reminder{inv.reminder_count > 1 ? "s" : ""} sent</span>}
                      </div>
                      {inv.reminder_count > 0 && inv.last_reminder_by && (
                        <p data-testid={`ar-last-reminder-${inv.id}`} className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1">
                          <Mail className="w-3 h-3" />Last sent by <span className="font-medium text-slate-500">{inv.last_reminder_by}</span>{inv.last_reminder_at ? ` · ${formatDateTime(inv.last_reminder_at)}` : ""}
                        </p>
                      )}
                      <p className="text-navy font-medium mt-1 flex items-center gap-1.5"><Building2 className="w-4 h-4 text-slate-400" />{inv.billing_company || inv.customer_name || "—"}</p>
                      {(inv.site_address || inv.project_name) && <p className="text-sm text-slate-500 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" />{inv.site_address || inv.project_name}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-heading font-bold text-navy">{money(inv.outstanding)}</p>
                      {inv.credit_applied > 0 ? (
                        <div className="text-xs mt-0.5 space-y-0.5" data-testid={`ar-credit-summary-${inv.id}`}>
                          <p className="text-slate-400">Original Total {money(inv.total_inc_gst)}</p>
                          <p className="text-red-600 font-medium" data-testid={`ar-credit-applied-${inv.id}`}>Credit Applied: -{money(inv.credit_applied)}</p>
                          <p className="text-navy font-semibold">Outstanding Balance {money(inv.outstanding)}</p>
                        </div>
                      ) : <p className="text-xs text-slate-400">Outstanding of {money(inv.total_inc_gst)}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm bg-slate-50/60 rounded-lg p-3">
                    <div><p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Invoice Date</p><p className="text-navy">{formatDate(inv.invoice_date)}</p></div>
                    <div><p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Due Date</p><p className="text-navy">{formatDate(inv.due_date)}</p></div>
                    <div><p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Payment Terms</p><p className="text-navy">{inv.payment_terms || "—"}</p></div>
                    <div><p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Xero Invoice</p><p className="text-navy">{inv.xero_status_display || "Awaiting Payment"}</p></div>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs bg-slate-50/60 rounded-lg px-3 py-2" data-testid={`ar-recipient-${inv.id}`}>
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    {inv.reminder_to ? (
                      <>
                        <span className="text-slate-500">Reminder will send to</span>
                        <span className="font-semibold text-navy">{inv.reminder_to}</span>
                        {(inv.reminder_cc || []).length > 0 && (
                          <span className="text-slate-500">· CC: <span className="text-slate-600">{(inv.reminder_cc || []).join(", ")}</span></span>
                        )}
                      </>
                    ) : (
                      <span className="text-red-600 font-medium flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" />No email on file — add one to the customer before sending.</span>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => setConfirmInv(inv)} disabled={busy === `reminder-${inv.id}` || !inv.reminder_to} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`ar-reminder-${inv.id}`}>
                      <Mail className="w-3.5 h-3.5 mr-1.5" />{busy === `reminder-${inv.id}` ? "Sending…" : "Send Reminder Email"}
                    </Button>
                    {inv.xero_invoice_id && !String(inv.xero_invoice_id).startsWith("XERO-SIM") && (
                      <Button size="sm" variant="outline" onClick={() => openInXero(inv)} className="text-[#13B5EA] border-[#13B5EA]/40" data-testid={`ar-open-xero-${inv.id}`}>
                        <ExternalLink className="w-3.5 h-3.5 mr-1.5" />View in Xero
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => refreshXero(inv)} disabled={busy === `refresh-${inv.id}`} data-testid={`ar-refresh-${inv.id}`}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1.5" />{busy === `refresh-${inv.id}` ? "Refreshing…" : "Refresh From Xero"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadInvoicePdf(inv)} data-testid={`ar-invoice-pdf-${inv.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1.5" />Invoice PDF
                    </Button>
                    {(inv.docket_ids || []).map((jid, i) => (
                      <Button key={jid} size="sm" variant="outline" onClick={() => downloadDocketPdf(jid, inv)} data-testid={`ar-docket-pdf-${inv.id}-${i}`}>
                        <FileText className="w-3.5 h-3.5 mr-1.5" />Docket {i + 1}
                      </Button>
                    ))}
                    <CreditNoteActions inv={inv} />
                    <Button size="sm" variant="outline" onClick={() => setCreditInv(inv)} className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`ar-credit-${inv.id}`}>
                      <ReceiptText className="w-3.5 h-3.5 mr-1.5" />Credit / Recharge
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAuditId(auditId === inv.id ? null : inv.id)} data-testid={`ar-audit-${inv.id}`}>
                      <History className="w-3.5 h-3.5 mr-1.5" />Audit History
                    </Button>
                  </div>

                  {auditId === inv.id && (
                    <div className="border border-slate-100 rounded-lg divide-y divide-slate-100" data-testid={`ar-audit-panel-${inv.id}`}>
                      {[...(inv.audit_log || [])].reverse().map((a, i) => (
                        <div key={i} className="px-3 py-2 text-xs flex items-start justify-between gap-3">
                          <div><p className="font-medium text-navy">{a.action}</p>{a.to && <p className="text-slate-400">To: {a.to}{a.cc && a.cc !== "—" ? ` · CC: ${a.cc}` : ""}</p>}</div>
                          <div className="text-right text-slate-400 shrink-0"><p>{formatDateTime(a.at)}</p><p>{a.admin}</p></div>
                        </div>
                      ))}
                      {(!inv.audit_log || inv.audit_log.length === 0) && <p className="px-3 py-2 text-xs text-slate-400">No history yet.</p>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(data.credited || []).length > 0 && (
        <div className="space-y-2" data-testid="ar-credited-section">
          <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Recently Fully Credited (not outstanding)</p>
          {data.credited.map((inv) => (
            <div key={inv.id} data-testid={`ar-credited-${inv.id}`} className="bg-slate-50 rounded-xl border border-slate-200 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-heading font-bold text-slate-500 line-through">{inv.invoice_number} · {money(inv.total_inc_gst)}</p>
                <p className="text-sm text-slate-500">{inv.billing_company || inv.customer_name}</p>
              </div>
              <div className="text-right">
                <span className="text-xs font-bold tracking-wider text-red-600 bg-red-50 border border-red-200 rounded-full px-2.5 py-1">FULLY CREDITED</span>
                {inv.recharged_to && <p className="text-xs text-slate-600 mt-1" data-testid={`ar-recharged-to-${inv.id}`}>Recharged as <span className="font-semibold text-navy">{inv.recharged_to.invoice_number}</span> to {inv.recharged_to.customer_name}</p>}
                {(inv.credit_notes || []).length > 0 && <p className="text-[11px] text-slate-400">{inv.credit_notes.map((c) => c.number).join(", ")} · {formatDate((inv.credited_at || "").slice(0, 10))}</p>}
              </div>
              <div className="w-full"><CreditNoteActions inv={inv} /></div>
            </div>
          ))}
        </div>
      )}

      <CreditRechargeDialog inv={creditInv} onClose={() => setCreditInv(null)} onDone={load} />

      <Dialog open={!!confirmInv} onOpenChange={(o) => !o && setConfirmInv(null)}>
        <DialogContent className="max-w-md" data-testid="reminder-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-navy"><Mail className="w-5 h-5 text-emerald-600" />Send Payment Reminder</DialogTitle>
          </DialogHeader>
          {confirmInv && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600">Send a payment reminder for <span className="font-semibold text-navy">{confirmInv.invoice_number}</span> ({money(confirmInv.outstanding)} outstanding) to:</p>
              <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 space-y-1">
                <p><span className="text-slate-400 text-xs uppercase tracking-wider font-semibold">To</span><br /><span className="font-semibold text-navy" data-testid="reminder-confirm-to">{confirmInv.reminder_to}</span></p>
                {(confirmInv.reminder_cc || []).length > 0 && (
                  <p className="pt-1"><span className="text-slate-400 text-xs uppercase tracking-wider font-semibold">CC</span><br /><span className="text-slate-600" data-testid="reminder-confirm-cc">{(confirmInv.reminder_cc || []).join(", ")}</span></p>
                )}
              </div>
              {confirmInv.reminder_count > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  A reminder was already sent {confirmInv.reminder_count} time{confirmInv.reminder_count > 1 ? "s" : ""}{confirmInv.last_reminder_at ? ` (last on ${formatDateTime(confirmInv.last_reminder_at)}${confirmInv.last_reminder_by ? ` by ${confirmInv.last_reminder_by}` : ""})` : ""}. This will send another.
                </p>
              )}
              <p className="text-[11px] text-slate-400">A copy is also BCC'd to your office inbox. This address comes from the customer's current details on file.</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmInv(null)} data-testid="reminder-confirm-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => sendReminder(confirmInv)} disabled={busy?.startsWith("reminder-")} data-testid="reminder-confirm-send">
              <Mail className="w-4 h-4 mr-2" />Send to this address
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
